<template>
    <section class="articles-page-top">
        <ResourcePageHeroShape v-if="!homeDocument.data.slices.length" class="articles-page-top__shape resource-page--default" />
        <div class="content">
            <Breadcrumbs class="breadcrumbs" />
            <PrismicRichText
                :field="homeDocument?.data?.pageTitle"
                :class="['page-title', {'page-title--without-slices': !homeDocument.data.slices.length}]"
            />
        </div>
    </section>
    <section class="articles-page-hero">
        <SliceZone v-if="homeDocument" :slices="homeDocument.data.slices" :components="components" />
    </section>
    <section class="articles-all">
        <PrismicRichText :field="homeDocument?.data?.listTitle" class="list-title" />
        <CategorySelector class="categories-filters" />
        <div v-if="articles?.results?.length" class="article-grid">
            <template v-for="(article, index) of articles.results as SuccessStoryDocument[]" :key="article.uid ?? ''">
                <ArticleCard :article="article" />
                <CTACard
                    v-if="homeDocument?.data.title && homeDocument.data.ctaLink && homeDocument.data.ctaText && index === 4"
                    class="cta"
                    :title="homeDocument.data.title"
                    :text="homeDocument.data.text"
                    :cta-link="homeDocument.data.ctaLink"
                    :cta-text="homeDocument.data.ctaText"
                />
            </template>
            <CTACard
                v-if="homeDocument?.data.title && homeDocument.data.ctaLink && homeDocument.data.ctaText && articles.results.length < 5"
                class="cta"
                :title="homeDocument.data.title"
                :text="homeDocument.data.text"
                :cta-link="homeDocument.data.ctaLink"
                :cta-text="homeDocument.data.ctaText"
            />
        </div>
        <p v-else class="text-center joy-mt-3 joy-color-neutral-40">{{ t('generic.no.results.found') }}</p>
        <Pagination
            v-if="articles?.total_pages > 1"
            base-path="/resources/success-stories"
            :total-pages="articles?.total_pages"
            :current-page="articles?.page"
        />
    </section>

    <SliceZone v-if="homeDocument" :slices="homeDocument.data.slices2" :components="components" />
</template>

<script lang="ts" setup>
    import {inject, ref} from 'vue';
    import {PrismicRichText, SliceZone} from '@prismicio/vue';
    import type {HomeSuccessStoriesDocument, ResourceCategoryDocument, SuccessStoryDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {baseSearchPredicatesByCustomType, createError, useAsyncData, useTranslation, useWebsitePageView} from '#imports';
    import type {AUDIENCES, CATEGORIES} from '~/app/keys';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import ArticleCard from '~/components/ArticleCard.vue';
    import Breadcrumbs from '~/components/Breadcrumbs.vue';
    import CategorySelector from '~/components/CategorySelector.vue';
    import CTACard from '~/components/CTACard.vue';
    import Pagination from '~/components/Pagination.vue';
    import ResourcePageHeroShape from '~/components/ResourcePageHeroShape.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';
    import {useDocumentMetadata} from '~/composables/useDocumentMetadata';
    import {components} from '~/slices/components';
    import {listingPageNumber} from '~/utils/listingPage';
    import {assertListingPageInBounds} from '~/utils/resourceNotFound';

    const {t} = useTranslation();

    const document = inject(PRISMIC_DOCUMENT, ref());
    const {
        params: {page},
        query,
    } = useRoute('resources-success-stories-page');
    const requestedPage = listingPageNumber(page);
    const queryParamResourceCategory = query.category ? (query.category as string) : undefined;
    const {data: resourceCategoriesFiltered} = await useAsyncData(
        'resource-categories-filtered',
        async () => {
            if (queryParamResourceCategory) {
                return await usePrismicSearch<ResourceCategoryDocument>({
                    type: 'resourceCategory',
                    uid: queryParamResourceCategory,
                });
            }
        },
        {deep: true},
    );
    const categoryId = resourceCategoriesFiltered.value?.results[0]?.id;
    const queryParamCategory = typeof query?.category === 'string' ? (query?.category as (typeof CATEGORIES)[number]) : undefined;
    const queryParamAudience = typeof query?.audience === 'string' ? (query?.audience as (typeof AUDIENCES)[number]) : undefined;
    const {data} = await useAsyncData(
        'success-stories',
        () =>
            Promise.all([
                usePrismicSearch<HomeSuccessStoriesDocument>({
                    type: 'homeSuccessStories',
                    audience: queryParamAudience ?? 'all',
                    category: queryParamCategory,
                    pageSize: 1,
                }),
                usePrismicSearch({
                    ...baseSearchPredicatesByCustomType.successStory,
                    type: 'successStory',
                    category: queryParamCategory,
                    audience: queryParamAudience !== 'all' ? queryParamAudience : undefined,
                    pageSize: 11,
                    page: requestedPage,
                    resourceCategoryId: categoryId,
                }),
            ]),
        {deep: true},
    );
    if (!data.value) {
        throw createError({
            message: `No success stories found for category ${queryParamCategory} and audience ${queryParamAudience}`,
            statusCode: 404,
            fatal: true,
        });
    }

    const [homeDocuments, articles] = data.value;
    assertListingPageInBounds(requestedPage, articles.total_pages);
    const homeDocument = ref(homeDocuments?.results[0]);
    if (!homeDocument.value) {
        const fallbackHomeDocuments = await usePrismicSearch({
            type: 'homeSuccessStories',
            pageSize: 1,
            audience: 'all',
            category: 'all',
        }); // fallback to default page : all audience and no category
        const fallbackHomeDocument = fallbackHomeDocuments?.results[0];
        if (!fallbackHomeDocument) {
            throw createError({
                message: `No home success story found for category ${queryParamCategory} and audience ${queryParamAudience}`,
                statusCode: 404,
                fatal: true,
            });
        }
        document.value = fallbackHomeDocument;
        homeDocument.value = fallbackHomeDocument as HomeSuccessStoriesDocument;
    } else {
        document.value = homeDocument.value;
    }

    if (homeDocument.value) {
        useDocumentMetadata(homeDocument.value);
    }

    // Tracking
    useWebsitePageView('resources-center', 'success-stories');
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .articles-page-hero .pinned-content-hero {
        padding: var(--joy-core-spacing-9) 0 80px;
    }

    .articles-page-top {
        padding-top: var(--joy-core-spacing-9);
        background-color: var(--joy-color-quaternary-10);
        position: relative;

        > .articles-page-top__shape {
            height: 220px;
            width: auto;
        }

        .breadcrumbs {
            margin-bottom: var(--joy-core-spacing-7);
        }

        .content {
            position: relative;
            z-index: 1;

            @include content-width;

            .page-title {
                h2 {
                    color: var(--joy-color-neutral-60);
                    font-family: var(--joy-font-family-title);
                    font-size: var(--joy-font-size-secondary-200);
                    font-weight: var(--joy-font-weight-normal);
                }

                &--without-slices {
                    padding-bottom: var(--joy-core-spacing-8);
                }
            }
        }
    }

    .articles-all {
        padding: 80px 0;

        @include content-width;

        .list-title {
            margin-bottom: var(--joy-core-spacing-5);
        }

        .categories-filters {
            margin-bottom: var(--joy-core-spacing-9);
        }

        .button-row {
            display: flex;
            justify-content: center;
            margin-bottom: var(--joy-core-spacing-11);

            @media (max-width: $mobile-breakpoint) {
                margin-bottom: var(joy-core-spacing-6);
            }
        }

        .article-grid {
            display: grid;
            row-gap: var(--joy-core-spacing-5);
            grid-template-columns: minmax(0, 1fr);
            margin-bottom: var(joy-core-spacing-12);

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--joy-core-spacing-4) var(--joy-core-spacing-3);
                margin-left: -15px;
                margin-right: -15px;
            }

            @include mq.screen_lg(min) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
                margin-bottom: 80px;
            }

            .cta {
                justify-self: stretch;
            }
        }
    }
</style>
